/**
 * Shared Net Exposure event grid (Match Odds / Fancy / Bookmaker + optional prem).
 * Market titles: Betfair (BF), custom BM/session (CM) — not Lab (PB).
 * Book Management → Detail Book; member tab → Details modal.
 */

import { useNavigate } from 'react-router-dom'
import { useViewerCurrencyRaw } from '@/api/hooks/shared/useViewerCurrency'
import { lineSessionMoneyOpts } from '@/features/net-exposure/lineSessionDisplayMoney'
import { money } from '@/lib/format'
import type {
  BookType,
  HorseAmount,
  NetExposureEvent,
  NetExposureMarket,
} from './fixture'
import { labeledMarketName } from './marketSourceLabel'
import {
  advanceMarketIdsFromEvent,
  advanceWorkstationPath,
  workstationSearchFromMarket,
  type WorkstationMarketType,
} from './workstation-fixture'

export type EventBooksActions = 'workstation' | 'details' | 'none'

export type EventBooksDetailsPayload = {
  marketId: string
  marketName: string
  /** Lab sportsbook vs Artemis Details. */
  source: 'sportsbook' | 'premium'
}

function toneClass(value: number): string {
  return value < 0 ? 'w7-ne-neg' : 'w7-ne-pos'
}

/** Lab list Amt: INR majors (Rs.) even for POINTS — no ÷100 Pts. */
function Amt({ value }: { value: number }) {
  const opts = lineSessionMoneyOpts(useViewerCurrencyRaw())
  return (
    <span className={`w7-ne-amt ${toneClass(value)}`}>
      {money(value, {
        round: true,
        symbol: false,
        currency: opts.currency,
        alreadyDisplay: opts.alreadyDisplay,
      })}
    </span>
  )
}

/** Market NE column on Backend-UI is shown as `netExposure * -1`. */
function MarketNe({ value }: { value: number }) {
  const shown = value * -1
  return <Amt value={shown} />
}

function horseAt(horse: HorseAmount[], index: number): number | null {
  return horse[index]?.amount ?? null
}

function MatchOddsCells({ market }: { market: NetExposureMarket }) {
  const h0 = horseAt(market.horse, 0)
  const h1 = horseAt(market.horse, 1)
  const h2 = horseAt(market.horse, 2)
  const hasDraw = market.horse.length >= 3
  return (
    <>
      <td className="w7-ne-cell w7-ne-cell-ne">
        <MarketNe value={market.netExposure} />
      </td>
      <td className="w7-ne-cell">
        {h0 == null ? <span>0</span> : <Amt value={h0} />}
      </td>
      <td className="w7-ne-cell w7-ne-cell-x">
        {hasDraw && h2 != null ? (
          <Amt value={h2} />
        ) : (
          <span className="w7-ne-dash">-</span>
        )}
      </td>
      <td className="w7-ne-cell">
        {h1 == null ? <span>0</span> : <Amt value={h1} />}
      </td>
    </>
  )
}

function DetailBookLink({
  marketId,
  marketName,
  eventName,
  type,
  book,
  sport,
}: {
  marketId: string
  marketName: string
  eventName: string
  type: WorkstationMarketType
  book: BookType
  sport?: string
}) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      className="w7-ne-ws"
      title="Detail Book"
      onClick={() =>
        navigate({
          pathname: `/net-exposure/workstation/${encodeURIComponent(marketId)}`,
          search: workstationSearchFromMarket({ marketName, eventName, type, book, sport }),
        })
      }
    >
      <img src="/net-exposure/net_exposure.png" alt="" width={25} height={17} />
    </button>
  )
}

function MarketAction({
  actions,
  market,
  eventName,
  type,
  book,
  sport,
  source,
  onDetails,
}: {
  actions: EventBooksActions
  market: NetExposureMarket
  eventName: string
  type: WorkstationMarketType
  book: BookType
  sport?: string
  source: 'sportsbook' | 'premium'
  onDetails?: (payload: EventBooksDetailsPayload) => void
}) {
  if (actions === 'none') return null
  if (actions === 'details') {
    // CM/Agent SQL matches `orders.vendor_market_id` — prefer marketId from books.
    const marketId = String(market.marketId || market.marketUniqueId || '')
    return (
      <button
        type="button"
        className="w7-ne-details"
        onClick={() =>
          onDetails?.({
            marketId,
            marketName: market.marketName,
            source,
          })
        }
      >
        Details
      </button>
    )
  }
  return (
    <DetailBookLink
      marketId={market.marketId}
      marketName={market.marketName}
      eventName={eventName}
      type={type}
      book={book}
      sport={sport}
    />
  )
}

export function EventBooksTable({
  event,
  book,
  actions = 'workstation',
  onDetails,
}: {
  event: NetExposureEvent
  book: BookType
  actions?: EventBooksActions
  onDetails?: (payload: EventBooksDetailsPayload) => void
}) {
  const navigate = useNavigate()
  const fancyRows = event.data.filter((m) => !m.bookMakerforNetExposure && !m.isMatchOdds)
  const bookmakerRows = event.data.filter((m) => m.bookMakerforNetExposure)
  const fancyPrem = (event.dataPrem ?? []).filter(
    (m) => !m.bookMakerforNetExposure && !m.isMatchOdds,
  )
  const bookmakerPrem = (event.dataPrem ?? []).filter((m) => m.bookMakerforNetExposure)
  const advanceIds = advanceMarketIdsFromEvent(event)
  const showViewTotal = actions === 'workstation' && advanceIds.length > 0

  const openViewTotal = () => {
    navigate({
      pathname: advanceWorkstationPath(advanceIds),
      search: new URLSearchParams({ event: event._id, book }).toString(),
    })
  }

  return (
    <table className="w7-ne-table">
      <colgroup>
        <col className="w7-ne-c-market" />
        <col className="w7-ne-c-icon" />
        <col className="w7-ne-c-ne" />
        <col className="w7-ne-c-runner" />
        <col className="w7-ne-c-runner" />
        <col className="w7-ne-c-runner" />
        <col className="w7-ne-c-fancy" />
        <col className="w7-ne-c-icon" />
        <col className="w7-ne-c-ne" />
      </colgroup>
      <tbody>
        <tr className="w7-ne-head">
          <th colSpan={2} className="w7-ne-event-title">
            <div className="w7-ne-event-title-inner">
              <span>{event._id}</span>
              {showViewTotal ? (
                <button
                  type="button"
                  className="w7-ne-view-total"
                  title="View Total — match odds + bookmakers"
                  onClick={openViewTotal}
                >
                  View Total
                </button>
              ) : null}
            </div>
          </th>
          <th className="w7-ne-col">Net Exposure</th>
          <th className="w7-ne-col">1</th>
          <th className="w7-ne-col">X</th>
          <th className="w7-ne-col">2</th>
          <th className="w7-ne-col-fancy">Fancy</th>
          <th className="w7-ne-col-icon" />
          <th className="w7-ne-col">Fancy Exposure</th>
        </tr>

        {event.matchOdds ? (
          <tr className="w7-ne-row">
            <td className="w7-ne-market">
              {labeledMarketName(event.matchOdds, 'match')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={event.matchOdds}
                eventName={event._id}
                type="match"
                book={book}
                sport={event.sport}
                source="sportsbook"
                onDetails={onDetails}
              />
            </td>
            <MatchOddsCells market={event.matchOdds} />
            <td colSpan={3} />
          </tr>
        ) : null}

        {fancyRows.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td colSpan={6} />
            <td className="w7-ne-market w7-ne-fancy-name">
              {labeledMarketName(bookRow, 'fancy')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={bookRow}
                eventName={event._id}
                type="fancy"
                book={book}
                sport={event.sport}
                source="sportsbook"
                onDetails={onDetails}
              />
            </td>
            <td className="w7-ne-cell w7-ne-cell-ne">
              <MarketNe value={bookRow.netExposure} />
            </td>
          </tr>
        ))}

        {bookmakerRows.map((bookRow) => (
          <tr key={bookRow.marketId} className="w7-ne-row">
            <td className="w7-ne-market">
              {labeledMarketName(bookRow, 'bookmaker')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={bookRow}
                eventName={event._id}
                type="bookmaker"
                book={book}
                sport={event.sport}
                source="sportsbook"
                onDetails={onDetails}
              />
            </td>
            <MatchOddsCells market={bookRow} />
            <td colSpan={3} />
          </tr>
        ))}

        {/* Prem / Artemis rows — same BF/CM tags as sportsbook (not Lab PB). */}
        {event.matchOddsPrem ? (
          <tr className="w7-ne-row">
            <td className="w7-ne-market">
              {labeledMarketName(event.matchOddsPrem, 'match')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={event.matchOddsPrem}
                eventName={event._id}
                type="match"
                book={book}
                sport={event.sport}
                source="premium"
                onDetails={onDetails}
              />
            </td>
            <MatchOddsCells market={event.matchOddsPrem} />
            <td colSpan={3} />
          </tr>
        ) : null}

        {fancyPrem.map((bookRow) => (
          <tr key={`pb-fancy-${bookRow.marketId}`} className="w7-ne-row">
            <td colSpan={6} />
            <td className="w7-ne-market w7-ne-fancy-name">
              {labeledMarketName(bookRow, 'fancy')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={bookRow}
                eventName={event._id}
                type="fancy"
                book={book}
                sport={event.sport}
                source="premium"
                onDetails={onDetails}
              />
            </td>
            <td className="w7-ne-cell w7-ne-cell-ne">
              <MarketNe value={bookRow.netExposure} />
            </td>
          </tr>
        ))}

        {bookmakerPrem.map((bookRow) => (
          <tr key={`pb-bm-${bookRow.marketId}`} className="w7-ne-row">
            <td className="w7-ne-market">
              {labeledMarketName(bookRow, 'bookmaker')}
            </td>
            <td className="w7-ne-icon-cell">
              <MarketAction
                actions={actions}
                market={bookRow}
                eventName={event._id}
                type="bookmaker"
                book={book}
                sport={event.sport}
                source="premium"
                onDetails={onDetails}
              />
            </td>
            <MatchOddsCells market={bookRow} />
            <td colSpan={3} />
          </tr>
        ))}
      </tbody>
    </table>
  )
}
